// Review draft · read and confirm (M3-F4; rows M3.8, M3.23, M3.39, M3.55; DC-74 / DC-90 / DC-122 / DC-180).
// Latest revision of the draft agreement, then every applicant confirms with the OTP sent to their own
// mobile; the revision counts as confirmed when all have. A new revision (any KYC or price change)
// clears every confirmation. "Something wrong?" → call your CRM before you confirm.
// Shows the PDF until Rohit's portal draft page (M12-F7) is ready.
import React, { useCallback, useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import { getReviewDraft, sendReviewOtp, confirmReview } from "../../../Services/Crm2Portal";
import { successTost } from "../../../Services/Toast";
import OtpEntry from "./OtpEntry";
import { niceDate } from "./Crm2Parts";
import css from "./Crm2Booking.module.css";
import useConsentPoint from "./TermsGate";

export default function ReviewDraftModal({ allotment, card, onClose, onDone, reload }) {
	const apId = allotment.allotmentPaymentId;
	const [rd, setRd] = useState(null);
	const [failed, setFailed] = useState(null);
	const [ticks, setTicks] = useState([false, false, false]);
	const [who, setWho] = useState(null); // applicant confirming now
	const [termsOk, setTermsOk] = useState(false);
	const { requirePoint, termsModal } = useConsentPoint();
	useEffect(() => {
		// M3-F11: Legal's terms on "review_confirm" first, if any
		requirePoint("review_confirm", apId, () => setTermsOk(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apId]);

	const load = useCallback(() => {
		getReviewDraft(apId)
			.then(setRd)
			.catch((err) => setFailed(err.message));
	}, [apId]);
	useEffect(() => load(), [load]);

	const allTicked = ticks.every(Boolean);
	const pending = (rd?.applicants || []).filter((a) => !a.confirmedAt);
	if (termsModal) return termsModal;
	return (
		<Modal open onClose={(_, r) => r !== "backdropClick" && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
			<Box className={`${css.modal} ${css.modalWide}`}>
				<div className={css.modalHeader}>
					<span>Review draft{rd?.revisionNo ? ` · Revision ${rd.revisionNo}` : ""}</span>
					<span className={css.modalClose} onClick={onClose} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>
				<div className={css.modalContent}>
					{failed && <div className={css.badBox}>{failed}</div>}
					{!rd && !failed && <p className={css.muted}>Loading…</p>}
					{rd && (
						<>
							{rd.revisionNo > 1 && <div className={css.info}>Revision {rd.revisionNo - 1} is no longer valid. Every applicant confirms Revision {rd.revisionNo}.</div>}
							{rd.link?.url ? <iframe className={css.docFrame} title="Review draft" src={rd.link.url} /> : <p className={css.muted}>The draft could not be shown. Please try again.</p>}
							{rd.link?.url && (
								<a className={css.lk} href={rd.link.url} target="_blank" rel="noreferrer">
									Download PDF
								</a>
							)}
							<div className={css.info}>
								<b>Something wrong?</b> A name, address or detail is not right: change it in your Agreement KYC (a new revision follows), or call your CRM
								{card?.crm ? ` ${card.crm.name}${card.crm.phone ? ` · ${card.crm.phone}` : ""}` : ""} before you confirm.
							</div>
							<div className={css.sl}>
								<div className={css.slH}>
									<b>Confirm by OTP · every applicant</b>
									<span>
										{rd.applicants.length - pending.length} of {rd.applicants.length} confirmed
									</span>
								</div>
								{rd.applicants.map((a) => (
									<div key={a.applicantId} className={`${css.sr} ${a.confirmedAt ? css.ok : css.now}`}>
										<i>{a.confirmedAt ? "✓" : "•"}</i>
										<div className={css.srM}>
											<div className={css.srT}>
												{a.no}. {a.name}
											</div>
											<div className={css.srS}>{a.confirmedAt ? `Confirmed ${niceDate(a.confirmedAt)}` : `OTP to ${a.maskedMobile}`}</div>
										</div>
										{!a.confirmedAt && who !== a.applicantId && (
											<div className={css.srA}>
												<span className={css.lk} onClick={() => setWho(a.applicantId)}>
													Confirm by OTP
												</span>
											</div>
										)}
									</div>
								))}
							</div>
							{who && (
								<>
									{["I have read the review draft.", "The names, addresses and PAN of every applicant are correct.", "The unit, price and payment details are correct."].map((t, i) => (
										<label key={t} className={css.tick}>
											<input type="checkbox" checked={ticks[i]} onChange={(e) => setTicks((x) => x.map((v, j) => (j === i ? e.target.checked : v)))} />
											{t}
										</label>
									))}
									<OtpEntry
										key={who}
										disabled={!allTicked || !termsOk}
										who={rd.applicants.find((a) => a.applicantId === who)?.name}
										sendLabel="Send OTP"
										confirmLabel={`Confirm Revision ${rd.revisionNo}`}
										send={() => sendReviewOtp(apId, rd.documentId, who)}
										verify={(challengeId, code) => confirmReview(apId, rd.documentId, who, challengeId, code)}
										onVerified={(res) => {
											setWho(null);
											if (res?.allConfirmed) {
												successTost(`Review draft Revision ${rd.revisionNo} confirmed by every applicant.`);
												onDone();
											} else {
												successTost("Confirmed. The other applicants confirm with their own OTP.");
												load();
												reload && reload();
											}
										}}
									/>
								</>
							)}
						</>
					)}
				</div>
			</Box>
		</Modal>
	);
}
